import React, { useState } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { track } from '@nexus/fff-analytics';

import { useFlags } from 'launchdarkly-react-client-sdk';
import styles from './SearchBar.module.css';

import { scrollToTopGradually } from '../../../shared-components/ScrollToTopButton/ScrollToTopButton';
import { selectPageTrackingFullData } from '../../../selectors/trackingSelector';
import { btnLocationEnum, segmentEnum } from '../../../utils/segment';
import {
  updateSearch,
  updateShowSearchBar,
} from 'services/actions/searchActions';
import { requestSearchProducts } from 'services/actions/productsActions';
import { useHistory } from 'react-router-dom';
import { removeQueryParam } from '../../../utils/url';

type InjectedProps = {|
  +viewSize: string,
|};

type StateProps = {|
  +productNames: {},
  +enableSearch: boolean,
  +showSearchBar: boolean,
|};

type DispatchProps = {|
  +updateSearch: typeof updateSearch,
  +updateShowSearchBar: typeof updateShowSearchBar,
|};

type Props = {|
  ...StateProps,
  ...DispatchProps,
  ...InjectedProps,
|};

const CTA_SEARCH_ICON = 'Search Icon';

const searchItems = (items, input, fieldName) => {
  input = input.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  const reg = new RegExp(input, 'i'); // consecutive letters
  return _.filter(items, (item) => {
    if (reg.test(item[fieldName]) && !item.gwp) {
      return item;
    }
  });
};

const renderProductName = (productName, query) => {
  const startIdx = productName.toLowerCase().indexOf(query);
  if (startIdx < 0) {
    // shouldn't happen just in case
    return productName;
  }
  const endIdx = startIdx + query.length;
  return (
    <span>
      {productName.substring(0, startIdx)}
      <b>{productName.substring(startIdx, endIdx)}</b>
      {productName.substring(endIdx)}
    </span>
  );
};

const SearchBar = ({
  productNames,
  trackingData,
  enableSearch,
  showSearchBar,
  updateSearch,
  updateShowSearchBar,
  viewSize,
  requestSearchProducts,
}: Props) => {
  const { rateAndReview: rateAndReviewFF } = useFlags();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const history = useHistory();
  if (!enableSearch || _.isEmpty(productNames)) {
    // productNames are empty if e.g. sale is closed
    return null;
  }
  const resultSize = _.size(results);

  function handleSearchTracking() {
    track({
      trackName: segmentEnum.searchStarted,
      properties: {
        ...trackingData,
        cta_name: CTA_SEARCH_ICON,
        experience: btnLocationEnum.search,
      },
    });
  }

  function handleSearchQueryTracking(query, resultSize) {
    track({
      trackName: segmentEnum.searchCompleted,
      properties: {
        ...trackingData,
        experience: btnLocationEnum.search,
        search_query: query,
        total_results: resultSize,
      },
    });
  }

  const clearState = () => {
    setQuery('');
    setResults({});
  };

  const handleChange = (event) => {
    setQuery(event.target.value.toLowerCase());
    setResults(searchItems(productNames, event.target.value, 'searchTerm'));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    displayResults(query, results);
  };

  const selectResult = (result) => {
    displayResults(result.searchTerm, [result]);
  };

  const displayResults = (query, searchResults) => {
    let results = _.chain(searchResults)
      .map('productIds')
      .flatten()
      .uniq()
      .map((r) => r.toString())
      .value();
    if (query) {
      removeProductLink();
      updateSearch({
        query,
        results,
      });
      requestSearchProducts({ productIds: results, rateAndReviewFF });
      scrollToTopGradually();
      handleSearchQueryTracking(query, searchResults.length);
    }
    closeSearchBar();
  };

  const openSearchBar = () => {
    clearState();
    updateShowSearchBar(true);
    handleSearchTracking();
  };

  const closeSearchBar = () => {
    updateShowSearchBar(false);
    clearState();
  };

  const removeProductLink = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('product_link')) {
      history.push(window.location.pathname + removeQueryParam('product_link'));
    }
  };

  return (
    <form
      action=""
      onBlur={() => {
        setTimeout(() => {
          closeSearchBar();
        }, 200); // wait a bit for selectResult to be dispatched
      }}
      onSubmit={handleSubmit}
      className={`${styles.icon} ${
        showSearchBar ? styles.open : styles.closed
      }`}
    >
      {!showSearchBar && (
        <button className="search" type="submit" onClick={openSearchBar}>
          <i className="far fa-search" />
        </button>
      )}
      {showSearchBar && (
        <div className={styles.searchBar}>
          <div className={styles.leftContent}>
            <div
              className={`${styles.formField} ${
                query ? styles.pinkBorder : ''
              }`}
            >
              <input
                value={query}
                placeholder={`${
                  viewSize === 'small'
                    ? 'Search'
                    : 'Search for keywords, products, and brands'
                }`}
                onChange={handleChange}
                type="search"
                autoFocus
              />
              <button
                type="button"
                onMouseDown={(e) => {
                  // use onMouseDown instead of onclick to prevent onblur from firing
                  e.preventDefault();
                  clearState();
                }}
                className={`${styles.clearButton} ${
                  !query ? styles.hidden : ''
                } `}
              >
                <i className="fas fa-times-circle" />
              </button>
            </div>
            {query && resultSize > 0 && (
              <ul className={styles.searchBarMatch}>
                {results.map((result, index) => {
                  return (
                    <li key={index} onClick={() => selectResult(result)}>
                      {renderProductName(result.searchTerm, query)}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className={styles.cancelLabel}>
            <label onClick={closeSearchBar}>Cancel</label>
          </div>
        </div>
      )}
    </form>
  );
};

function mapStateToProps(state) {
  return {
    trackingData: selectPageTrackingFullData(state),
    enableSearch: state.searchState.enableSearch,
    showSearchBar: state.searchState.showSearchBar,
    productNames: state.searchState.searchTerms,
  };
}

export default connect(mapStateToProps, {
  updateSearch,
  updateShowSearchBar,
  requestSearchProducts,
})(SearchBar);
